import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getToken } from '../utils/auth';
import { FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaDatabase, FaSyncAlt, FaChartLine, FaEnvelopeOpenText } from 'react-icons/fa';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { fetchSheetData } from "../utils/auth";
import { generateReport } from "../utils/auth";
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const ReportsDashboard = () => {
  const [sheets, setSheets] = useState([]);
  const [sheetMetadata, setSheetMetadata] = useState({});
  const [currentSheet, setCurrentSheet] = useState('');
  const [reportData, setReportData] = useState([]);
  const [timestamp, setTimestamp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const formatTimestamp = (ts) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    } catch {
      return '--';
    }
  };

  const fetchReport = useCallback(async (sheetName) => {
    if (!sheetName) {
      setReportData([]);
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const res = await generateReport(sheetName, token);
      setReportData(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch {
      setError('Error loading report');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSheetList = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetchSheetData(token);
      const result = Array.isArray(res.data) ? res.data : [];
      const metadata = {};
      result.forEach(sheet => { metadata[sheet["Sheet Name"]] = sheet["Timestamp"]; });

      const lastSheet = result[result.length - 1]?.["Sheet Name"];
      setSheets(result.map(s => s["Sheet Name"]));
      setSheetMetadata(metadata);
      setCurrentSheet(lastSheet || '');
      setTimestamp(metadata[lastSheet] || '');
      fetchReport(lastSheet);
      setError('');
    } catch {
      setError('Error loading sheet list');
    }
  }, [fetchReport]);

  const onSheetChange = (e) => {
    const selected = e.target.value;
    setCurrentSheet(selected);
    setTimestamp(sheetMetadata[selected]);
    fetchReport(selected);
  };

  const getCounts = () => {
    const total = reportData?.length;
    const sent = reportData?.filter(d => d.Status?.toLowerCase() === 'success').length;
    const pending = reportData?.filter(d => d.Status?.toLowerCase() === 'pending').length;
    const failed = reportData?.filter(d => d.Status?.toLowerCase() === 'failed').length;
    const percent = (val) => total === 0 ? "0.0" : ((val / total) * 100).toFixed(1);
    return { total, sent, pending, failed, sentP: percent(sent), pendingP: percent(pending), failedP: percent(failed) };
  };

  useEffect(() => { loadSheetList(); }, [loadSheetList]);
  const { total, sent, pending, failed, sentP, pendingP, failedP } = getCounts();
  const deliveryRate = sentP;

  const doughnutData = useMemo(() => ({
    labels: ['Sent', 'Pending', 'Failed'],
    datasets: [{
      data: [sent, pending, failed],
      backgroundColor: ['#22c55e', '#f59e0b', '#ef4444'],
      borderColor: 'rgba(13, 34, 57, 0.9)',
      borderWidth: 4,
      hoverOffset: 6
    }]
  }), [sent, pending, failed]);

  const barData = useMemo(() => ({
    labels: ['Sent', 'Pending', 'Failed'],
    datasets: [{
      label: 'Email Status Count',
      data: [sent, pending, failed],
      backgroundColor: ['rgba(34, 197, 94, 0.86)', 'rgba(245, 158, 11, 0.86)', 'rgba(239, 68, 68, 0.86)'],
      borderRadius: 8
    }]
  }), [sent, pending, failed]);

  const filteredReportData = useMemo(() => {
    const query = search.toLowerCase();

    return reportData.filter(row =>
      row.Email?.toLowerCase().includes(query) ||
      row.Name?.toLowerCase().includes(query)
    );
  }, [reportData, search]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'rgba(238, 247, 255, 0.86)',
          boxWidth: 12,
          padding: 16,
        },
      },
    },
  }), []);

  const barOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { ticks: { color: 'rgba(226, 239, 250, 0.76)' }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { color: 'rgba(226, 239, 250, 0.76)', precision: 0 }, grid: { color: 'rgba(180,211,242,0.12)' } }
    }
  }), []);

  const ProgressBar = ({ value, tone = "info" }) => (
    <div className="report-progress" aria-label={`${value}%`}>
      <div className={`report-progress-fill ${tone}`} style={{ width: `${value}%` }} />
      <span>{value}%</span>
    </div>
  );

  const MetricCard = ({ label, value, percent, icon: Icon, tone }) => (
    <div className={`report-metric-card ${tone}`}>
      <div className="report-metric-top">
        <span>{label}</span>
        <Icon />
      </div>
      <strong>{value}</strong>
      {percent !== undefined && <ProgressBar value={percent} tone={tone} />}
    </div>
  );

  return (
    <div className="reports-dashboard">
        <div className="report-hero">
          <div>
            <span className="report-eyebrow">Campaign performance</span>
            <h2 className="section-title report-title">
              Delivery Report
              {loading && <span className="report-title-loader" aria-label="Loading report" />}
            </h2>
            <p className="section-subtitle">Track delivery health, recipient outcomes, and send progress for uploaded campaign sheets.</p>
          </div>
          <div className="report-score-card">
            <FaChartLine />
            <div>
              <span>Delivery rate</span>
              <strong>{deliveryRate}%</strong>
            </div>
          </div>
        </div>

        {error && <div className="dashboard-error mb-3">{error}</div>}

        <div className="report-controls">
          <div className="report-control-group">
            <label htmlFor="sheet-select">Campaign sheet</label>
            <select id="sheet-select" className="form-select form-select-sm" value={currentSheet} onChange={onSheetChange}>
              {sheets.map((sheet, i) => <option key={i} value={sheet}>{sheet}</option>)}
            </select>
          </div>
          <div className="report-trigger-meta">
            <span>Triggered On</span>
            <strong>{formatTimestamp(timestamp)}</strong>
          </div>
          <div className="dashboard-actions ms-auto">
            <button className="btn btn-outline-primary btn-sm" onClick={loadSheetList}>Refresh All</button>
            <button className="btn btn-info btn-sm" onClick={() => fetchReport(currentSheet)} disabled={!currentSheet || loading}>
              {loading ? <><FaSyncAlt className="me-1 spin" /> Refreshing</> : <><FaSyncAlt className="me-1" /> Refresh</>}
            </button>
          </div>
        </div>

        <div className="report-metrics-grid">
          <MetricCard label="Total recipients" value={total} icon={FaDatabase} tone="info" />
          <MetricCard label="Delivered" value={sent} percent={sentP} icon={FaCheckCircle} tone="success" />
          <MetricCard label="Pending" value={pending} percent={pendingP} icon={FaHourglassHalf} tone="warning" />
          <MetricCard label="Failed" value={failed} percent={failedP} icon={FaTimesCircle} tone="danger" />
        </div>

        <div className="report-analytics-grid">
            <div className="dashboard-card report-chart-card">
              <h3 className="chart-title">Status Distribution</h3>
              <div className="report-doughnut-wrap">
                <Doughnut data={doughnutData} options={{ ...chartOptions, cutout: '68%' }} />
                <div className="report-doughnut-center">
                  <strong>{deliveryRate}%</strong>
                  <span>Delivered</span>
                </div>
              </div>
            </div>

            <div className="dashboard-card report-chart-card">
              <h3 className="chart-title">Status Comparison</h3>
              <div className="report-bar-wrap">
                <Bar 
                  data={{
                    ...barData,
                    datasets: barData.datasets.map(ds => ({
                      ...ds,
                      barThickness: 40,
                      maxBarThickness: 40
                    }))
                  }}
                  options={barOptions} 
                />
              </div>
            </div>
        </div>

        <section className="report-table-panel">
          <div className="report-table-header">
            <div>
              <h3>Recipient Activity</h3>
              <p>{filteredReportData.length} of {total} recipients shown</p>
            </div>
            <div className="report-search">
              <FaEnvelopeOpenText />
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by email or name" 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {filteredReportData.length === 0 ? (
            <div className="dashboard-empty">No recipient records match this view.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm align-middle dashboard-table report-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReportData.map((row, idx) => {
                      const status = row.Status?.toLowerCase() || 'unknown';
                      const badgeClass =
                        status === 'success' ? 'report-status success' :
                        status === 'pending' ? 'report-status warning' :
                        status === 'failed' ? 'report-status danger' : 'report-status muted';
                      return (
                        <tr key={`${row.Email || 'recipient'}-${idx}`}>
                          <td className="report-email">{row.Email || '--'}</td>
                          <td>{row.Name || '--'}</td>
                          <td>{row.Company || '--'}</td>
                          <td><span className={badgeClass}>{row.Status || 'Unknown'}</span></td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </section>
    </div>
  );
};

export default ReportsDashboard;
